import { useEffect, useRef } from "react"
import { useBaseMap } from "../BaseMap"
import JobMetro from "../../../modules/crm/base/models/JobMetro"
import { JobMetroLayerTypes } from "../../../core/Enums"

interface IProps {
    metros?: JobMetro[]
    /** Drawn stronger than the rest. */
    selectedMetroId?: string
    /** A click on a metro's area. Without it the areas are only shown. */
    onSelect?: (metro: JobMetro) => void
    /** Each metro's name in the middle of its area, once the map is zoomed in far enough to read it. */
    labels?: boolean
}

// How each area is drawn: the picked metro clearly, the others as a hint of where they are, and
// the one under the pointer in between, so it reads as something to click.
const styleFor = (color: string | undefined, state: "selected" | "hover" | "normal") => {
    const c = color || "#1b84ff"
    if (state === "selected") return { color: c, fillColor: c, weight: 2, opacity: .9, fillOpacity: .22 }
    if (state === "hover") return { color: c, fillColor: c, weight: 2, opacity: .7, fillOpacity: .14 }
    return { color: c, fillColor: c, weight: 1, opacity: .35, fillOpacity: .06 }
}

/** The metros' areas -- circles, rectangles and polygons, as they were drawn in the metro editor. */
export default function MetroLayers(props: IProps) {

    const { L, map, zoom } = useBaseMap()

    const onSelect = useRef(props.onSelect)
    onSelect.current = props.onSelect
    const selectedRef = useRef(props.selectedMetroId)
    selectedRef.current = props.selectedMetroId

    // Every Leaflet shape by metro id; a metro may be drawn as several shapes.
    const shapesRef = useRef<Record<string, any[]>>({})

    const restyle = (metroId: string, hover?: boolean) => {
        const metro = props.metros?.find(m => m.id === metroId)
        const state = metroId === selectedRef.current ? "selected" : hover ? "hover" : "normal"
        shapesRef.current[metroId]?.forEach(s => s.setStyle(styleFor(metro?.color, state)))
    }

    useEffect(() => {
        const shapes: Record<string, any[]> = {}

        props.metros?.forEach(metro => {
            if (!metro.id) return
            const id = metro.id
            // Only clickable when somebody listens; otherwise clicks go through to the map.
            const style = { ...styleFor(metro.color, id === selectedRef.current ? "selected" : "normal"), interactive: !!props.onSelect }

            metro.layers?.forEach(layer => {
                let shape: any
                if (layer.type === JobMetroLayerTypes.Circle && layer.position) shape = L.circle(layer.position, { ...style, radius: layer.circleRadius })
                else if (layer.type === JobMetroLayerTypes.Rectangle && layer.positions) shape = L.rectangle(layer.positions, style)
                else if (layer.type === JobMetroLayerTypes.Polygon && layer.positions) shape = L.polygon(layer.positions, style)
                if (!shape) return

                shape.on("mouseover", () => restyle(id, true))
                shape.on("mouseout", () => restyle(id))
                shape.on("click", () => onSelect.current?.(metro))
                shape.addTo(map)
                ;(shapes[id] = shapes[id] ?? []).push(shape)
            })
        })

        shapesRef.current = shapes
        return () => {
            Object.values(shapes).flat().forEach(s => s.remove())
            shapesRef.current = {}
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, props.metros])

    // A new pick restyles in place and puts the picked metro on top of its neighbours.
    useEffect(() => {
        Object.keys(shapesRef.current).forEach(id => restyle(id))
        if (props.selectedMetroId) shapesRef.current[props.selectedMetroId]?.forEach(s => s.bringToFront())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedMetroId, props.metros])

    // Names: none when zoomed far out (they would cover each other), small, then large.
    useEffect(() => {
        const all = Object.entries(shapesRef.current)
        all.forEach(([id, shapes]) => {
            const metro = props.metros?.find(m => m.id === id)
            shapes.forEach((s, i) => {
                s.unbindTooltip()
                if (!metro?.name) return
                // Only the first shape of a metro carries the label; a metro drawn as three pieces
                // is still one name.
                if (props.labels && zoom >= 7 && i === 0) {
                    s.bindTooltip(metro.name, {
                        permanent: true, direction: "center", interactive: false,
                        className: zoom < 10 ? "metroname-tooltip-small" : "metroname-tooltip-large"
                    }).openTooltip()
                }
                else s.bindTooltip(metro.name, { sticky: true })
            })
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zoom, props.labels, props.metros, map])

    return null
}
